import { Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { NgFor, NgIf } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CallLogResponse } from '../../../models/responses/call-log-response';
import { AiConfigurationPageEventsService } from '../../../events/ai-configuration-page-events.service';
import { CallLogService } from '../../../services/call-log.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import moment from 'moment-timezone';
import { MatIconModule } from '@angular/material/icon';
import { getStandardDateFormat } from '../../../utilities/date-utils';

@Component({
  selector: 'app-call-log-list',
  standalone: true,
  imports: [
    MatDividerModule,
    MatDialogModule,
    MatButtonModule,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    NgFor,
    NgIf,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatIconModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './call-log-list.component.html',
  styleUrl: './call-log-list.component.scss'
})
export class CallLogListComponent {
  @Input() callLogResponses!: CallLogResponse[];
  @Input() isCampaignReview!:boolean;
  displayedColumns: string[] = ['Phone','Date', 'Outcome', 'UserSentiment', 'View', 'Delete'];
  dataSource!: MatTableDataSource<CallLogResponse>;
  outcomes: string[] = ['ALL'];
  outcomeSelectedValue: FormControl = new FormControl('ALL');
  startDateRangePickerValue: FormControl = new FormControl();
  endDateRangePickerValue: FormControl = new FormControl();
  isLoading: boolean = false;
  isLoadingArray: boolean[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private aiAgentPageEvent: AiConfigurationPageEventsService,
    private callLogService: CallLogService,
    private snackBar: MatSnackBar
  ) {
    this.dataSource = new MatTableDataSource();
  }

  ngOnInit() {
    this.initTableData();
    this.setDateRangeFilter();
  }

  initTableData() {
    this.dataSource.data = this.callLogResponses;
    this.isLoadingArray = Array.from({ length: this.callLogResponses.length }, () => false);
    this.outcomes = ['ALL', ...new Set(this.callLogResponses.map(cl => cl.outcome))];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['callLogResponses'].currentValue) {
      this.initTableData();
    }
  }

  onClearFilterClicked(){
    this.endDateRangePickerValue.reset();
    this.startDateRangePickerValue.reset();
    this.outcomeSelectedValue.setValue('ALL');

    this.dataSource.data = this.callLogResponses;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onViewClick(callLog: CallLogResponse) {
    if(this.isCampaignReview){
      this.aiAgentPageEvent.onCampaignCallLogViewClickedEvent.emit(callLog)
    }else{
      this.aiAgentPageEvent.onCallLogViewClickedEvent.emit(callLog);
    }
  }

  onBackButtonClicked(){
    this.aiAgentPageEvent.onCallLogBackClickedEvent.emit(null)
  }

  applyFilter() {
    this.dataSource.data = this.callLogResponses.filter(cl => {
      return cl.outcome.includes(this.outcomeSelectedValue.value == 'ALL' ? '' : this.outcomeSelectedValue.value);
    });
  }

  setDateRangeFilter(){
    this.endDateRangePickerValue.valueChanges.subscribe(() => {
      this.dataSource.data = this.callLogResponses.filter(cl => {
        const d = moment(cl.startTime);
        const start = moment(this.startDateRangePickerValue.value);
        const end = moment(this.endDateRangePickerValue.value);

        return cl.outcome.includes(this.outcomeSelectedValue.value == 'ALL' ? '' : this.outcomeSelectedValue.value) &&
          d.isBetween(start, end, null, '[]');
      });
    });
  }

  onDeleteCallLog(callLog: CallLogResponse, index: number) {
    this.isLoading = true;
    this.isLoadingArray[index] = true;

    this.callLogService.removeById(callLog.id).subscribe({
      next: () => {
        this.aiAgentPageEvent.onCallLogViewDeletedEvent.emit();
        this.snackBar.open('Call log deleted successfully', 'Close', { duration: 5000 });
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        alert('Server error, contact support');
      },
    });
  }

  // TODO figure out what is the best date format to render
  getDateFormat(startTime: Date) {
    return getStandardDateFormat(startTime);
  }
}
